import { computed, inject, Injectable, linkedSignal, resource } from '@angular/core';
import { CashbookRepository } from '../repositories/cashbook.repository';
import { CartService } from './cart.service';
import { CashbookEntry, CashbookEntryType } from '@softapps/wws/pos-core';

@Injectable()
export class CartCashbookService {
  readonly #repository = inject(CashbookRepository);
  readonly #cartService = inject(CartService);

  readonly #params = linkedSignal(() => {
    const number = this.#cartService.cart()?.account?.number;
    return number;
  });

  readonly types = resource({
    params: () => this.#params(),
    loader: async () => this.#repository.getTypes(),
  });

  readonly cashbookEntries = computed(() => {
    return this.#cartService.cart()?.cashbookEntries ?? [];
  });

  readonly typesWithEntries = computed(() => {
    const cashbookEntries = this.cashbookEntries();
    const entryTypes = this.types.hasValue() ? this.types.value() : null;

    if (!entryTypes) return [];

    return entryTypes
      .map((type) => ({
        type,
        entries: cashbookEntries.filter((o) => o.type.id === type.id) ?? [],
      }))
      .filter((o) => o.entries.length > 0);
  });

  async loadTypes(): Promise<CashbookEntryType[]> {
    const types = await this.#repository.getTypes();
    this.types.set(types);
    return types;
  }

  async create(cartId: number, entry: Partial<CashbookEntry>): Promise<void> {
    const updatedCart = await this.#repository.create(cartId, entry);
    this.#cartService.setCart(updatedCart);
  }

  async update(cartId: number, entry: Partial<CashbookEntry>): Promise<void> {
    const updatedCart = await this.#repository.update(cartId, entry);
    this.#cartService.setCart(updatedCart);
  }

  async remove(cartId: number, entry: CashbookEntry): Promise<void> {
    const updatedCart = await this.#repository.remove(cartId, entry.id);
    this.#cartService.setCart(updatedCart);
  }
}
